import algoliasearch from "algoliasearch";
import {
  Descriptions,
  Input,
  DatePicker,
  TimePicker,
  Select,
  Popconfirm,
  Button,
  notification,
  Space,
  Spin,
} from "antd";
// import firebase from "firebase/app";
import { doc, getFirestore, Timestamp } from "firebase/firestore";
import { Mode } from "firestorter";
import { observer } from "mobx-react-lite";
import moment, { Moment } from "moment";
import React, { useEffect, useState, ChangeEvent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import config from "../../config";
import {
  Administration,
  useStore,
  WorkOrder,
  WorkOrderData,
  Invitation,
  CustomerData,
  HourTypeData,
  LocationData,
  ProductData,
  VehicleData,
  EmployeeData,
} from "../../store";
import SearchSelect from "../general/select";

const styles = {
  input: {
    width: "100%",
  },
  content: {
    width: "100%",
  },
  right: {
    float: "right" as const,
  },
};

interface Props {
  administration: Administration;
  order: WorkOrder;
  onCancel?: () => void;
}

const TIME_FORMAT = "HH:mm";

const Detail = (props: Props) => {
  const { administration } = props;
  const { employees } = administration;

  const searchClient = useMemo(
    () => algoliasearch(config.algolia.appId, config.algolia.apiKey),
    []
  );

  const workorderData = (data: WorkOrderData) => {
    const { startAt, endAt, ...otherData } = data;
    return {
      startAt:
        startAt || Timestamp.fromDate(moment().startOf("day").hour(9).toDate()),
      endAt:
        endAt || Timestamp.fromDate(moment().startOf("day").hour(17).toDate()),
      ...otherData,
    };
  };

  const [data, setData] = useState(workorderData(props.order.data));
  const { t } = useTranslation();
  const store = useStore();
  const { auth } = store;

  useEffect(() => {
    setData(workorderData(props.order.data));
  }, [props.order.data]);

  const update = (key: string) => (value: any) => {
    const newData = { ...data, [key]: value };
    setData(newData);
    return newData;
  };

  const updateInput = (key: string) => (
    event: ChangeEvent<HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    update(key)(value);
  };

  const updateSelect = (key: string) => (value: string) => {
    const firestore = getFirestore();
    const ref = doc(firestore, value);
    update(key)(ref);
  };

  const updateTime = (key: string) => (value: Moment | null) => {
    const timestamp = Timestamp.fromDate(value?.toDate() || new Date());
    const newData = { ...data, [key]: timestamp };

    const startAt = moment(newData.startAt?.toDate());
    const endAt = moment(newData.endAt?.toDate());
    const duration = moment.duration(endAt.diff(startAt));

    if (duration.asDays() > 1) {
      newData["endAt"] = Timestamp.fromDate(
        moment(endAt).subtract(Math.floor(duration.asDays()), "days").toDate()
      );
    }
    setData(newData);
  };

  const updateDuration = (value: Moment | null) => {
    const { startAt } = data;
    const hours = value?.hours();
    const minutes = value?.minutes();
    const endAt = Timestamp.fromDate(
      moment(startAt?.toDate())
        .add(hours, "hours")
        .add(minutes, "minutes")
        .toDate()
    );
    update("endAt")(endAt);
  };

  const remove = async () => {
    try {
      await props.order?.delete();
      props.onCancel && props.onCancel();
    } catch (error) {
      const message = (error as Error)?.message;
      notification.error({
        message: t("Remove order failed"),
        description: message,
      });
    }
  };

  const save = async () => {
    try {
      if (!auth.user?.id) throw new Error("Not authenticated");
      if (!data.employee) throw new Error("No employee selected");

      if (!data.createdAt) data.createdAt = Timestamp.now();
      if (!data.createdBy) {
        const invitation = new Invitation(
          `${data.employee.path}/invitation/0`,
          { mode: Mode.Off },
          store
        );
        await invitation.fetch();
        data.createdBy = invitation.userId || auth.user?.id;
      }
      await props.order?.set(data);
      notification.success({
        message: t("Saved order successfully"),
      });
      props.onCancel && props.onCancel();
    } catch (error) {
      const message = (error as Error)?.message;
      notification.error({
        message: t("Save order failed"),
        description: message,
      });
    }
  };

  const cancel = () => {
    setData(props.order.data);
    props.onCancel && props.onCancel();
  };

  const hours = () => {
    const startAt = moment(data.startAt?.toDate());
    const endAt = moment(data.endAt?.toDate());
    if (endAt.isBefore(startAt)) {
      endAt.add(24, "hour");
    }
    const duration = moment.duration(endAt.diff(startAt));
    return `${duration.hours()}:${`${duration.minutes()}`.padStart(2, "0")}`;
  };

  const disabledDate = (current: Moment) => {
    return moment().endOf("day").isBefore(current);
  };

  const searchEmployees = async (query: string = "") => {
    const index = searchClient.initIndex(`employee_${administration.id}`);
    const results = await index.search<EmployeeData>(query, {
      hitsPerPage: 1000,
    });
    return results.hits.map((hit) => ({
      key: hit.objectID,
      value: `multivers/${administration.id}/employee/${hit.objectID}`,
      label: hit.name,
    }));
  };

  const searchCustomers = async (query: string = "") => {
    const index = searchClient.initIndex(`customer_${administration.id}`);
    const results = await index.search<CustomerData>(query, {
      hitsPerPage: 1000,
    });
    return results.hits.map((hit) => ({
      key: hit.objectID,
      value: `multivers/${administration.id}/customer/${hit.objectID}`,
      label: hit.name,
    }));
  };

  const searchVehicles = async (query: string = "") => {
    const index = searchClient.initIndex(`project_${administration.id}`);
    const results = await index.search<VehicleData>(query, {
      hitsPerPage: 1000,
    });
    return results.hits.map((hit) => ({
      key: hit.objectID,
      value: `multivers/${administration.id}/project/${hit.objectID}`,
      label: hit.projectId,
    }));
  };

  const searchLocations = async (query: string = "") => {
    const index = searchClient.initIndex(`location_${administration.id}`);
    const results = await index.search<LocationData>(query, {
      hitsPerPage: 1000,
    });
    return results.hits.map((hit) => ({
      key: hit.objectID,
      value: `multivers/${administration.id}/location/${hit.objectID}`,
      label: hit.VVLOCATIE,
    }));
  };

  const searchProducts = async (query: string = "") => {
    const index = searchClient.initIndex(`product_${administration.id}`);
    const results = await index.search<ProductData>(query, {
      hitsPerPage: 1000,
    });
    return results.hits.map((hit) => ({
      key: hit.objectID,
      value: `multivers/${administration.id}/product/${hit.objectID}`,
      label: hit.description,
    }));
  };

  const searchHourTypes = async (query: string = "") => {
    const index = searchClient.initIndex(`hourType_${administration.id}`);
    const results = await index.search<HourTypeData>(query, {
      hitsPerPage: 1000,
    });
    return results.hits.map((hit) => ({
      key: hit.objectID,
      value: `multivers/${administration.id}/hourType/${hit.objectID}`,
      label: hit.description,
    }));
  };

  return (
    <Space direction="vertical" style={styles.content}>
      <Descriptions column={1} bordered>
        <Descriptions.Item label={t("Werkbon Id")}>
          <Input value={props.order.id} style={styles.input} disabled />
        </Descriptions.Item>
        <Descriptions.Item label={t("Date")}>
          <DatePicker
            format="LL"
            value={moment(data.startAt?.toDate())}
            onChange={updateTime("startAt")}
            style={styles.input}
            disabledDate={disabledDate}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t("User")}>
          <SearchSelect
            value={employees.isLoaded ? data.employee?.path : <Spin />}
            style={styles.input}
            onChange={updateSelect("employee")}
            fetchOptions={searchEmployees}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t("Start")}>
          <TimePicker
            value={moment(data.startAt?.toDate(), TIME_FORMAT)}
            format={TIME_FORMAT}
            minuteStep={5}
            onChange={updateTime("startAt")}
            style={styles.input}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t("End")}>
          <TimePicker
            value={moment(data.endAt?.toDate(), TIME_FORMAT)}
            format={TIME_FORMAT}
            minuteStep={5}
            onChange={updateTime("endAt")}
            style={styles.input}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t("Hours")}>
          <TimePicker
            value={moment(hours(), TIME_FORMAT)}
            format={TIME_FORMAT}
            minuteStep={5}
            showNow={false}
            onChange={updateDuration}
            style={styles.input}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t("Day/night")}>
          <Select
            value={data.daynight}
            style={styles.input}
            onChange={update("daynight")}
          >
            <Select.Option value="day">{t("Day")}</Select.Option>
            <Select.Option value="night">{t("Night")}</Select.Option>
          </Select>
        </Descriptions.Item>
        <Descriptions.Item label={t("Customer")}>
          <SearchSelect
            value={data.customer?.path}
            style={styles.input}
            onChange={updateSelect("customer")}
            fetchOptions={searchCustomers}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t("Vehicle")}>
          <SearchSelect
            value={data.vehicle?.path}
            style={styles.input}
            onChange={updateSelect("vehicle")}
            fetchOptions={searchVehicles}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t("Location")}>
          <SearchSelect
            value={data.location?.path}
            style={styles.input}
            onChange={updateSelect("location")}
            fetchOptions={searchLocations}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t("Product")}>
          <SearchSelect
            value={data.product?.path}
            style={styles.input}
            onChange={updateSelect("product")}
            fetchOptions={searchProducts}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t("Activity")}>
          <SearchSelect
            value={data.hourType?.path}
            style={styles.input}
            onChange={updateSelect("hourType")}
            fetchOptions={searchHourTypes}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t("Remarks")}>
          <Input.TextArea
            rows={4}
            value={data.remarks}
            onChange={updateInput("remarks")}
            style={styles.input}
          />
        </Descriptions.Item>
      </Descriptions>
      <div style={styles.content}>
        <Popconfirm
          title={t(
            "This operation cannot be reversed. Are you sure you want to delete this work order?"
          )}
          okText={t("Delete")}
          okButtonProps={{ danger: true }}
          onConfirm={remove}
          cancelText={t("Cancel")}
        >
          <Button danger>{t("Delete")}</Button>
        </Popconfirm>
        <Space style={styles.right}>
          <Button onClick={cancel}>{t("Cancel")}</Button>
          <Button type="primary" onClick={save}>
            {t("Save")}
          </Button>
        </Space>
      </div>
    </Space>
  );
};

export default observer(Detail);
